import {Component, HostListener, OnInit} from '@angular/core';
import {BasicFilters} from '../../model/filters/basic-filters';
import {SortOrder} from '../../model/sort-order';
import {ApplicationSubscription} from '../../model/application-subscription';
import {ApplicationSubscriptionsService} from '../../services/application-subscriptions.service';
import {JsonEditorService} from '../../services/json-editor.service';
import {TenantsService} from '../../services/tenants.service';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslocoService} from '@ngneat/transloco';
import {CommonMessagesService} from '../../services/common-messages.service';
import {ApplicationSubscriptionFilters} from '../../model/filters/application-subscription-filters';
import {AuthGuard} from '../../common/auth-guard.service';
import {Tenant} from '../../model/tenant';
import {FiltersService} from "../../services/filters.service";

@Component({
  selector: 'app-subscriptions',
  templateUrl: './subscriptions.component.html',
  styleUrls: ['./subscriptions.component.scss']
})
export class SubscriptionsComponent implements OnInit {

  public subscriptions: ApplicationSubscription[];
  public totalSubscriptions: number;
  public loadingSubscriptions: boolean;
  public tenants: Tenant[];

  public rows = 10;
  public first = 0;
  public selected = [];

  public subscriptionTypes: any[];
  public protocols: any[];

  public filtersVisible: boolean;
  public currentFilters: ApplicationSubscriptionFilters;

  public JsonEditorUtils = JsonEditorService;
  public filtersFilled: boolean;

  public activeOptions = [
    { label: 'applications.subscriptions.enabled', value: true },
    { label: 'applications.subscriptions.disabled', value: false },
  ];

  constructor(private subscriptionsService: ApplicationSubscriptionsService,
              private tenantsService: TenantsService,
              private messageService: MessageService,
              private i18n: TranslocoService,
              public authGuardService: AuthGuard,
              private confirmationService: ConfirmationService,
              private commonMessagesService: CommonMessagesService,
              private filtersService: FiltersService<ApplicationSubscriptionFilters>) {
    this.currentFilters = new ApplicationSubscriptionFilters();
    this.subscriptionTypes = ['EVENTS', 'COMPLEX_EVENTS', 'MEASUREMENTS', 'PROPERTIES'];
    this.protocols = ['AMQP_SERVER', 'HTTPS', 'INSTALLED_BROKER', 'KAFKA',
      'AZURE_KAFKA_EVENT_HUB', 'EMQX', 'FIWARE_IOT_AGENT_JSON',
      'SENTILO', 'INSTALLED_BROKER_NOTIFICATIONS'];
  }

  ngOnInit(): void {
    if (this.filtersService.getCurrentFilters(this.constructor.name) !== undefined) {
      this.filtersFilled = true;
    }
    this.subscriptions = new Array(this.rows);
    if (this.authGuardService.superAdminRequired()) {
      this.tenantsService.getTenants().subscribe(response => {
        this.tenants = response.tenants
          .sort((a, b) => a.name > b.name ? 1 : -1);
        Object.assign(this.currentFilters, this.filtersService.getCurrentFilters(this.constructor.name));
      });
    } else {
      Object.assign(this.currentFilters, this.filtersService.getCurrentFilters(this.constructor.name));
    }
  }

  private loadSubscriptions(offset?: number, limit?: number): void {
    this.loadingSubscriptions = true;
    this.subscriptions = new Array(this.rows);
    this.trimFieldsForm(this.currentFilters);
    this.subscriptionsService.getSubscriptions(offset, limit, this.filtersService.getCurrentFilters(this.constructor.name) === undefined ? this.currentFilters : this.filtersService.getCurrentFilters(this.constructor.name))
      .subscribe(response => {
        this.subscriptions = response.applicationSubscriptions;
        let dataKey = 0;
        this.subscriptions.forEach(subscription => {
          if (subscription.appliedTo) {
            subscription.appliedTo = JSON.parse(subscription.appliedTo);
          }
          if (subscription.protocolConfig) {
            subscription.protocolConfig = JSON.parse(subscription.protocolConfig);
          }
          subscription['dataKey'] = dataKey++;
        });
        this.totalSubscriptions = response.totalElements;
        this.loadingSubscriptions = false;
      });
  }

  onPageChange(event): void {
    this.currentFilters.sortField = event.sortField;
    this.currentFilters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
    this.loadSubscriptions(event.first / event.rows, event.rows);
  }

  resetFilters(): void {
    this.currentFilters = new ApplicationSubscriptionFilters();
    this.currentFilters.sortField = 'name';
    this.currentFilters.sortOrder = SortOrder.ASCENDING;
    this.filtersFilled = false;
    this.filtersService.setCurrentFilters(undefined, this.constructor.name);
    this.applyFilters(true);
  }

  applyFilters(reset?: boolean): void {
    this.first = 0;
    if (reset !== true) {
      this.filtersService.setCurrentFilters(this.currentFilters, this.constructor.name);
      this.filtersFilled = true;
    }
    this.loadSubscriptions(this.first / this.rows, this.rows);
  }

  toggleSubscription({name, tenant, active: oldIsActive}: ApplicationSubscription): void {
    this.subscriptionsService.toggletApplicationSubscription(name, tenant.uuid).subscribe({
      next: () => this.subscriptions.find(subscription => subscription.name === name).active = !oldIsActive,
      error: () => this.commonMessagesService.showUnexpectedPersistingError('edit')
    });
  }

  confirmAndDelete(applicationSubscription: ApplicationSubscription): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => {
      this.deleteApplicationSubscription(applicationSubscription.tenant.uuid, applicationSubscription.name);
    });
  }

  deleteApplicationSubscription(tenantUuid: string, name: string): void {
    this.tenantsService.deleteTenantApplicationSubscription(tenantUuid, name).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadSubscriptions(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status);
    });
  }

  confirmAndDeleteSelected(): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => this.deleteSelected(), true);
  }

  deleteSelected(): void {
    if (this.selected.length === 0) {
      return;
    }
    const identifiers = this.selected.map(item => ({tenantUuid: item.tenant.uuid, name: item.name}));

    this.subscriptionsService.deleteApplicationSubscriptions(identifiers).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadSubscriptions(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status, true);
    });
  }

  trimFieldsForm(filters: BasicFilters): void {
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (event.key === 'f') {
      this.filtersVisible = true;
    }
    if (event.key === 'Escape') {
      this.filtersVisible = false;
    }
  }

}
